import React, { useMemo } from "react";
import type { EChartsOption } from "echarts";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import { useQuery } from "@tanstack/react-query";
import { fetchTokensHistoryPoints, fetchTokensHistoryRatios } from "@/services/queries/tokens/tokensScreenerQueries";
import { MetricKeys } from "@/constants/metrics";
import { correlationMatrix } from "@/lib/metrics";

// Stablecoin filtering handled upstream

type RangeKey = '1d' | '7d' | '30d' | '365d';

const RANGE_TO_POINTS: Record<RangeKey, number> = {
  '1d': 2, // minimum to compute one daily return
  '7d': 8,  // ~7 daily changes
  '30d': 31,
  '365d': 366,
};

type Props = {
  rows: Array<Record<string, unknown>>;
  range: RangeKey;
  xKey: string;
  search: string;
  mcMin: number | '';
  mcMax: number | '';
  labelFromCatalog: (key: string) => string;
};

const CorrelationChart: React.FC<Props> = ({ rows, range, xKey, search, mcMin, mcMax,  labelFromCatalog }) => {
  const selectedTickers = useMemo(() => {
    const items: Array<{ ticker: string; mc: number }> = [];
    for (const r of rows as Array<Record<string, unknown>>) {
      const ticker = String(r["tokenSymbol"] ?? "");
      if (!ticker) continue;
      // excludeStables handled upstream
      if (search) {
        const name = String(r["tokenName"] ?? r["name"] ?? "");
        const s = search.toLowerCase();
        if (!ticker.toLowerCase().includes(s) && !name.toLowerCase().includes(s)) continue;
      }
      const mcRaw = (r["market_cap"] as number | null) ?? (r[MetricKeys.MarketCap] as number | null) ?? null;
      const mc = mcRaw == null ? NaN : Number(mcRaw);
      if (!Number.isFinite(mc) || mc <= 0) continue;
      if (mcMin !== '' && mc < Number(mcMin)) continue;
      if (mcMax !== '' && mc > Number(mcMax)) continue;
      items.push({ ticker, mc });
    }
    const MAX = 15;
    return items.sort((a, b) => b.mc - a.mc).slice(0, MAX).map((x) => x.ticker);
  }, [rows, search, mcMin, mcMax]);

  const effectiveMetricKey = xKey || MetricKeys.PriceClose;
  const tickersCsv = useMemo(() => selectedTickers.join(','), [selectedTickers]);
  const isRatioMetric = effectiveMetricKey.startsWith('ratio:');

  const historyQuery = useQuery({
    queryKey: ['tokens', 'history', isRatioMetric ? 'ratios' : 'points', 'correlation', tickersCsv, effectiveMetricKey],
    queryFn: ({ signal }) => {
      if (isRatioMetric) {
        return fetchTokensHistoryRatios({ tickersCsv, ratioKeysCsv: effectiveMetricKey, signal });
      } else {
        return fetchTokensHistoryPoints({ tickersCsv, metricKeysCsv: effectiveMetricKey, signal });
      }
    },
    enabled: Boolean(tickersCsv && effectiveMetricKey),
    staleTime: 60_000,
  });

  const option = useMemo<EChartsOption>(() => {
    type Series = { dates: Array<string | number>; values: Array<number | null> };
    const byTicker = new Map<string, Series>();
    
    // Process the multi-ticker response
    const historyData = historyQuery.data || [];
    for (const tokenData of historyData) {
      const t = tokenData.ticker;
      const m = tokenData.metrics?.[0];
      if (!t || !m) continue;
      byTicker.set(t, { dates: m.dates || [], values: m.values || [] });
    }

    // Parse/normalize dates to ms and align union
    const parseDateToKey = (d: string | number): number => {
      if (typeof d === 'number') {
        if (d >= 10000101 && d <= 99991231) {
          const y = Math.floor(d / 10000);
          const m = Math.floor((d % 10000) / 100);
          const day = d % 100;
          return Date.UTC(y, m - 1, day);
        }
        return d < 1e12 ? d * 1000 : d;
      }
      const p = Date.parse(d);
      return Number.isFinite(p) ? p : 0;
    };

    const dateSet = new Set<number>();
    for (const t of selectedTickers) {
      const s = byTicker.get(t);
      for (const dd of (s?.dates || [])) dateSet.add(parseDateToKey(dd));
    }
    const allDates = Array.from(dateSet).filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b);

    // Need at least 3 points to compute 2 daily changes; otherwise matrix trends to 0
    const pointsNeeded = Math.max(3, RANGE_TO_POINTS[range] || 31);
    const datesSlice = allDates.slice(-pointsNeeded);
    const dateToIndex = new Map<number, number>();
    for (let i = 0; i < allDates.length; i++) dateToIndex.set(allDates[i]!, i);
    const sliceIndices: number[] = datesSlice.map((d) => dateToIndex.get(d) ?? -1);

    // Build aligned daily return series per ticker
    const returnSeriesList: number[][] = [];
    const labels: string[] = [];
    for (const t of selectedTickers) {
      const s = byTicker.get(t);
      if (!s) continue;
      const map = new Map<number, number>();
      for (let i = 0; i < s.dates.length; i++) {
        const k = parseDateToKey(s.dates[i]!);
        const v = Number(s.values[i] ?? null);
        if (Number.isFinite(k) && Number.isFinite(v)) map.set(k, v);
      }
      const alignedFull: number[] = new Array(allDates.length).fill(NaN);
      for (let i = 0; i < allDates.length; i++) {
        const v = map.get(allDates[i]!);
        alignedFull[i] = (typeof v === 'number' && Number.isFinite(v)) ? v : NaN;
      }
      // Compute daily percent changes
      const daily: number[] = new Array(allDates.length).fill(NaN);
      for (let i = 1; i < allDates.length; i++) {
        const a = alignedFull[i];
        const b = alignedFull[i - 1];
        if (Number.isFinite(a) && Number.isFinite(b) && Math.abs(b as number) > 0) {
          daily[i] = ((a as number) - (b as number)) / Math.abs(b as number);
        }
      }
      const returnsSlice: number[] = sliceIndices.map((idx) => (idx >= 0 ? daily[idx]! : NaN)).filter((v) => Number.isFinite(v)) as number[];
      if (returnsSlice.length >= 2) {
        returnSeriesList.push(returnsSlice);
        labels.push(t);
      }
    }

    // Compute correlation matrix
    const matrix = correlationMatrix(returnSeriesList);
    const data: Array<[number, number, number]> = [];
    for (let i = 0; i < matrix.length; i++) {
      for (let j = 0; j < matrix.length; j++) {
        const v = Number((matrix[i]?.[j] ?? 0).toFixed(2));
        data.push([i, j, v]);
      }
    }

    const option: EChartsOption = {
      backgroundColor: 'transparent',
      tooltip: {
        position: 'top',
        formatter: (p: unknown) => {
          const pr = p as { value: [number, number, number] };
          const [i, j, v] = pr.value;
          const a = labels[i] ?? '';
          const b = labels[j] ?? '';
          const s = Number(v) > 0 ? '+' : '';
          const metricLabel = labelFromCatalog(String(xKey));
          return `${a} vs ${b} — ${metricLabel}<br/>${s}${Number(v).toFixed(2)}`;
        }
      },
      grid: { left: 84, right: 14, top: 14, bottom: 72 },
      xAxis: { type: 'category', data: labels, axisLabel: { color: '#9ca3af', rotate: 30, align: 'right', fontSize: 11, margin: 12 }, axisTick: { show: false }, axisLine: { show: false } },
      yAxis: { type: 'category', data: labels, axisLabel: { color: '#9ca3af', fontSize: 11 }, axisTick: { show: false }, axisLine: { show: false } },
      visualMap: { min: -1, max: 1, calculable: true, orient: 'horizontal', left: 'center', bottom: 0, inRange: { color: ['rgba(239,68,68,0.28)', 'rgba(15,23,42,0.12)', 'rgba(34,197,94,0.28)'] } },
      series: [{
        name: 'Correlation',
        type: 'heatmap',
        data,
        label: {
          show: true,
          color: '#e5e7eb',
          fontSize: 11,
          formatter: (p: { value: [number, number, number] }) => {
            const v = Number(p.value?.[2] ?? 0);
            const s = v > 0 ? '+' : '';
            return `${s}${v.toFixed(2)}`;
          }
        },
        itemStyle: { borderColor: 'rgba(148,163,184,0.18)', borderWidth: 1 },
      }],
    } as EChartsOption;

    return option;
  }, [historyQuery.data, selectedTickers, xKey, range, labelFromCatalog]);

  const hasData = useMemo(() => {
    const historyData = historyQuery.data || [];
    return historyData.some((tokenData) => {
      const vals = tokenData.metrics?.[0]?.values;
      return Array.isArray(vals) && vals.length > 1; // at least 2 points to form a change
    });
  }, [historyQuery.data]);

  const isLoading = historyQuery.isLoading;
  const error = historyQuery.error ? (historyQuery.error as Error).message : null;
  return (
    <EChartsRenderer
      chartOption={option}
      height={640}
      isLoading={isLoading}
      error={error}
      hasData={hasData}
      containerClassName="bg-transparent border-0 rounded-lg overflow-hidden"
    />
  );
};

export default CorrelationChart;

